/**
 * HIDDEN motion module (spec: docs/specs/screen-homepage.md, gate 2026-07-28).
 *
 * 1. <hidden-wave-text> — splits its text into per-character spans and reveals them
 *    with a staggered slide-up ("wave") on first intersection. CSS lives in
 *    src/input.css (.wave-char). Native implementation — no GSAP dependency.
 * 2. Lenis smooth-scroll easing, site-wide (assets/lenis.min.js exposes globalThis.Lenis).
 *
 * Both are skipped under prefers-reduced-motion and inside the theme editor —
 * section fade-in is core's scroll-reveal.js (data-scroll-reveal), not this module.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = window.Shopify?.designMode === true;

class HiddenWaveText extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  static #observer = null;

  static #observe(el) {
    HiddenWaveText.#observer ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            HiddenWaveText.#observer?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.3 }
    );
    HiddenWaveText.#observer.observe(el);
  }

  connectedCallback() {
    if (reducedMotion || designMode || this.classList.contains('is-split')) return;

    const text = this.textContent ?? '';
    this.setAttribute('aria-label', text.trim());
    this.classList.add('is-split');

    const fragment = document.createDocumentFragment();
    let index = 0;
    // Split into words so wrapping stays natural; chars animate inside each word.
    for (const word of text.split(/(\s+)/)) {
      if (/^\s+$/.test(word)) {
        fragment.append(document.createTextNode(' '));
        continue;
      }
      const wordSpan = document.createElement('span');
      wordSpan.className = 'wave-word';
      wordSpan.setAttribute('aria-hidden', 'true');
      for (const char of word) {
        const charSpan = document.createElement('span');
        charSpan.className = 'wave-char';
        charSpan.style.setProperty('--char-i', String(index));
        charSpan.textContent = char;
        wordSpan.append(charSpan);
        index += 1;
      }
      fragment.append(wordSpan);
    }
    this.replaceChildren(fragment);
    HiddenWaveText.#observe(this);
  }
}

if (!customElements.get('hidden-wave-text')) {
  customElements.define('hidden-wave-text', HiddenWaveText);
}

if (!reducedMotion && !designMode && typeof globalThis.Lenis === 'function' && !globalThis.__hiddenLenis) {
  // lerp 0.17 matches the reference site (jurassicworldexperience.com/sg — Salient
  // theme, Lenis at strength 50 → 0.17). Requires the lenis.css compat rules in
  // src/input.css: without `scroll-behavior: auto` the browser re-eases Lenis'
  // per-frame writes (double smoothing) and lerp tuning has almost no effect.
  // `prevent` is the exemption list: any app overlay that scrolls its own content has
  // to be named here, or Lenis swallows the wheel (non-passive listener on window,
  // preventDefault on every event) and the overlay never scrolls while the page
  // behind it keeps moving. .evey-modal covers both of Evey's — the date picker and
  // the attendee-info form shown at checkout, which was unusable without this.
  const lenis = new globalThis.Lenis({
    lerp: 0.17,
    smoothWheel: true,
    prevent: (node) => node.closest('.ReactModalPortal, .evey-modal, .hidden-cart-drawer') !== null,
  });
  globalThis.__hiddenLenis = lenis;
  const raf = (time) => {
    lenis.raf(time);
    requestAnimationFrame(raf);
  };
  requestAnimationFrame(raf);
}
